import React, { useState, useEffect } from "react";
import {
  AlertTriangle, Eye, EyeOff, CircleUser, Lock, BadgeCheck, ShieldCheck,
  Users, CalendarDays, Wallet, Clock, Award, UserCheck, TrendingUp,
  ScanFace, FileText, Fingerprint,
} from "lucide-react";
import api from "../api";
import { uploadUrl } from "../uploadUrl";
import { K } from "../theme";

// Subset of HR icons scattered across the hero header as a subtle on-brand
// pattern — mirrors the desktop login so the two read as one product.
const HERO_ICONS = [
  { I: Users,        top: "10%", left: "10%", size: 34, op: 0.16, rot: -12 },
  { I: CalendarDays, top: "14%", left: "74%", size: 30, op: 0.14, rot: 8 },
  { I: Wallet,       top: "38%", left: "26%", size: 26, op: 0.12, rot: -6 },
  { I: Clock,        top: "30%", left: "88%", size: 28, op: 0.12, rot: 10 },
  { I: Award,        top: "60%", left: "14%", size: 30, op: 0.13, rot: -10 },
  { I: ScanFace,     top: "54%", left: "62%", size: 26, op: 0.11, rot: 4 },
  { I: UserCheck,    top: "72%", left: "84%", size: 30, op: 0.12, rot: -8 },
  { I: TrendingUp,   top: "78%", left: "36%", size: 26, op: 0.11, rot: 12 },
  { I: FileText,     top: "20%", left: "44%", size: 24, op: 0.10, rot: -4 },
  { I: Fingerprint,  top: "62%", left: "44%", size: 24, op: 0.10, rot: 8 },
];

export const Login = ({ onLogin }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [showP, setShowP] = useState(false);
  const [brand, setBrand] = useState({ logo_url: null, company_name: null });

  // Branding (public) so the screen is on-brand before sign-in.
  useEffect(() => {
    api.get("/auth/branding")
      .then(d => setBrand({ logo_url: d?.logo_url || null, company_name: d?.company_name || null }))
      .catch(() => {});
  }, []);

  // Same code → user-friendly message map as the desktop Login.
  // Keeps the raw JS error out of the mobile employee's face.
  const humanize = (err) => {
    switch (err.code) {
      case 'NETWORK':         return "Can't reach the sign-in server. Check your internet, then try again.";
      case 'MISSING_FIELDS':  return "Please enter both your username and password.";
      case 'BAD_CREDENTIALS': {
        let m = "Wrong username or password.";
        if (typeof err.remaining === 'number' && err.remaining <= 3) {
          m += err.remaining === 0
            ? " Next failure will lock sign-in for 15 minutes."
            : ` ${err.remaining} attempt${err.remaining === 1 ? '' : 's'} left before sign-in is locked for 15 minutes.`;
        }
        return m + " Forgotten? Contact HR.";
      }
      case 'LOCKED':          return `Too many failed sign-ins. Wait about ${err.retryAfterMin || 15} minute${(err.retryAfterMin || 15) === 1 ? '' : 's'} and try again.`;
      case 'SERVER':          return "Something went wrong on our side. Try again shortly.";
      default:                return err.message || "Sign-in didn't work. Try again.";
    }
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!u.trim() || !p) { setErr("Please enter both your username and password."); return; }
    setLoading(true); setErr("");
    try {
      const d = await api.login(u, p);
      onLogin(d);
    } catch (er) {
      setErr(humanize(er));
    } finally {
      setLoading(false);
    }
  };

  const logoSrc = brand.logo_url ? uploadUrl(brand.logo_url) : null;
  const Wordmark = () => (
    <div className="inline-flex items-baseline font-extrabold tracking-tight" style={{ fontSize: 26 }}>
      <span style={{ color: K.t0 }}>zucol</span>
      <span style={{ color: K.p }}>.</span>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-bg1">
      {/* ── Branded hero header ── */}
      <div
        className="relative overflow-hidden px-6 pt-12 pb-12 text-center flex-shrink-0"
        style={{ background: `linear-gradient(150deg, ${K.p} 0%, #1E3A8A 55%, #1E3A8A 120%)` }}
      >
        <div className="absolute inset-0 pointer-events-none select-none">
          {HERO_ICONS.map((h, i) => (
            <div key={i} className="absolute" style={{ top: h.top, left: h.left, transform: `rotate(${h.rot}deg)`, opacity: h.op }}>
              <h.I size={h.size} color="#ffffff" strokeWidth={1.5} />
            </div>
          ))}
        </div>
        <div className="absolute -top-16 -left-12 w-56 h-56 rounded-full" style={{ background: "rgba(255,255,255,0.12)", filter: "blur(40px)" }} />
        <div className="absolute -bottom-20 -right-10 w-64 h-64 rounded-full" style={{ background: "rgba(255,255,255,0.10)", filter: "blur(45px)" }} />

        <div className="relative z-10">
          <div className="inline-flex items-center justify-center bg-white rounded-2xl px-5 py-[10px] shadow-[0_10px_30px_rgba(0,0,0,0.18)] mb-5">
            {logoSrc
              ? <img src={logoSrc} alt={brand.company_name || "Logo"} style={{ maxHeight: 40, maxWidth: 170 }} className="object-contain" />
              : <Wordmark />}
          </div>
          <h1 className="text-white font-extrabold leading-tight m-0 text-[22px]">Human Resource<br />Management System</h1>
          <p className="text-white/85 text-[14px] mt-2">Your people operation, in your pocket.</p>
        </div>
      </div>

      {/* ── Form card (overlaps the hero) ── */}
      <div className="flex-1 px-5 -mt-7 relative z-10 pb-8">
        <div className="bg-bg1 border border-b0 rounded-2xl shadow-[0_12px_34px_rgba(0,0,0,0.10)] p-5 max-w-[440px] mx-auto">
          <h2 className="text-t0 font-extrabold m-0 text-[19px]">Welcome back</h2>
          <p className="text-t2 text-[14px] mt-1 mb-5">Sign in to your {brand.company_name || "Zucol HR"} account</p>

          <form onSubmit={submit}>
            <div className="mb-4">
              <label className="block text-[14px] font-semibold text-t2 mb-[6px] uppercase tracking-[0.05em]">Username</label>
              <div className="relative">
                <CircleUser size={16} className="absolute left-[12px] top-1/2 -translate-y-1/2 text-t3 pointer-events-none" />
                <input
                  type="text"
                  autoComplete="username"
                  placeholder="you@company.in"
                  value={u}
                  onChange={e => setU(e.target.value)}
                  className="w-full pl-[38px] pr-3 py-[13px] bg-bg2 border border-b0 rounded-xl text-t0 text-[16px] outline-none box-border focus:border-[#2563EB] transition-colors"
                />
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-[14px] font-semibold text-t2 mb-[6px] uppercase tracking-[0.05em]">Password</label>
              <div className="relative">
                <Lock size={16} className="absolute left-[12px] top-1/2 -translate-y-1/2 text-t3 pointer-events-none" />
                <input
                  type={showP ? "text" : "password"}
                  autoComplete="current-password"
                  placeholder="••••••••"
                  value={p}
                  onChange={e => setP(e.target.value)}
                  className="w-full pl-[38px] pr-[44px] py-[13px] bg-bg2 border border-b0 rounded-xl text-t0 text-[16px] outline-none box-border focus:border-[#2563EB] transition-colors"
                />
                <button
                  type="button"
                  onClick={() => setShowP(v => !v)}
                  aria-label={showP ? "Hide password" : "Show password"}
                  className="absolute right-[10px] top-1/2 -translate-y-1/2 bg-transparent border-0 cursor-pointer text-t3 p-2 flex items-center"
                >
                  {showP ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {err && (
              <div className="flex items-center gap-2 text-[14px] text-r bg-rb border border-rd rounded-lg px-3 py-2 mb-4">
                <AlertTriangle size={14} className="flex-shrink-0" />
                <span>{err}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !u || !p}
              className="w-full flex items-center justify-center gap-2 py-[14px] min-h-[48px] rounded-xl border-0 cursor-pointer text-white text-[16px] font-bold transition-opacity disabled:opacity-60"
              style={{ background: `linear-gradient(135deg,${K.p},#1E3A8A)`, boxShadow: "0 8px 20px rgba(37,99,235,0.30)" }}
            >
              {loading ? "Signing in…" : <><BadgeCheck size={17} /> Sign In</>}
            </button>
          </form>

          <div className="mt-6 flex items-center gap-[6px] justify-center text-t3 text-[14px]">
            <ShieldCheck size={12} />
            <span>Secure sign-in · Your session is encrypted</span>
          </div>
        </div>

        <div className="text-center text-t3 text-[14px] mt-5">
          © {new Date().getFullYear()} {brand.company_name || "Zucol"} · For the full experience, visit on desktop
        </div>
      </div>
    </div>
  );
};
